import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import UploadPage from './pages/UploadPage';
import SplitPage from './pages/SplitPage';
import ReviewPage from './pages/ReviewPage';
import ReportPage from './pages/ReportPage';
import ExportPage from './pages/ExportPage';
import ExemptionPage from './pages/ExemptionPage';
import { authApi } from './services/api';

// Auth verification handler for SSO redirect
const AuthVerify: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyAzureToken, checkAuth } = useApp();
  const [error, setError] = React.useState<string | null>(null);
  
  useEffect(() => {
    const jwt = searchParams.get('jwt');
    
    if (jwt) {
      verifyAzureToken(jwt)
        .then(() => {
          return checkAuth();
        })
        .then(() => {
          navigate('/upload', { replace: true });
        })
        .catch((err) => {
          console.error('SSO verification error:', err);
          setError('认证令牌验证失败，请重试');
          setTimeout(() => navigate('/login', { replace: true }), 2000);
        });
    } else {
      navigate('/login', { replace: true });
    }
  }, [searchParams, navigate, checkAuth, verifyAzureToken]);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="text-center">
        {error ? (
          <>
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-rose-600 text-2xl">✕</span>
            </div>
            <p className="text-rose-600">{error}</p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">正在接管统一网关会话...</p>
          </>
        )}
      </div>
    </div>
  );
};

// Protected route wrapper
const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useApp();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="w-16 h-16 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  
  return <>{children}</>;
};

// Error boundary component
const ErrorDisplay: React.FC = () => {
  const { error, clearError } = useApp();
  
  if (!error) return null;
  
  return (
    <div className="fixed bottom-4 right-4 bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg shadow-lg max-w-md z-50">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <p className="font-medium">错误</p>
          <p className="text-sm">{error}</p>
        </div>
        <button 
          onClick={clearError}
          className="text-rose-500 hover:text-rose-700"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/verify" element={<AuthVerify />} />
        
        {/* Redirect /upload to project route */}
        <Route path="/upload" element={<Navigate to="/project/new/upload" replace />} />
        
        {/* Project Routes Wrapped in Layout */}
        <Route path="/project/:id/*" element={
          <RequireAuth>
            <Layout>
              <Routes>
                <Route path="upload" element={<UploadPage />} />
                <Route path="split" element={<SplitPage />} />
                <Route path="exemptions" element={<ExemptionPage />} />
                <Route path="review" element={<ReviewPage />} />
                <Route path="report" element={<ReportPage />} />
                <Route path="export" element={<ExportPage />} />
              </Routes>
            </Layout>
          </RequireAuth>
        } />

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      <ErrorDisplay />
    </>
  );
};

const App: React.FC = () => {
  return (
    <AppProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AppProvider>
  );
};

export default App;
